import { CSSProperties, KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Grow up to this many pixels of text, then scroll inside the composer. */
const MAX_INPUT_HEIGHT = 132;

/**
 * ChatComposer — the message input row: a --surface pill holding a borderless
 * textarea and a circular solid --text send button, with an optional quiet
 * safety disclaimer line underneath.
 *
 * Enter sends, Shift+Enter inserts a newline. Controlled via value/onChange.
 */
export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  /** Disables input + send (e.g. while sending). */
  disabled?: boolean;
  /** Quiet shield line under the composer. Pass null to hide. */
  disclaimer?: string | null;
  /** Stops typing and pasting at this many characters. No limit when omitted. */
  maxLength?: number;
  /** A slimmer box and a one-line notice, for phones with the keyboard open. */
  compact?: boolean;
  style?: CSSProperties;
}

export function ChatComposer({
  value,
  onChange,
  onSend,
  placeholder = 'Message…',
  disabled = false,
  disclaimer = 'Never share passport numbers, banking details, or home address in chat.',
  maxLength,
  compact = false,
  style,
}: ChatComposerProps) {
  const [hover, setHover] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [multiline, setMultiline] = useState(false);

  // Most callers disable the composer for the duration of onSend (e.g.
  // while the message round-trips to the server) — disabling a focused
  // textarea blurs it natively, and nothing re-focused it once sending
  // finished. That's what forced a click back into the box after every
  // message: hit Enter, disabled flips true then false, focus never
  // returns. Refocus on the false transition, but only if nothing else
  // has since taken focus (e.g. the send button, or the user tabbed away).
  const wasDisabled = useRef(disabled);
  useEffect(() => {
    if (wasDisabled.current && !disabled) {
      const el = inputRef.current;
      if (el && (document.activeElement === document.body || document.activeElement === null)) {
        // preventScroll: the composer is already in view; letting the focus
        // scroll here makes iOS push the whole page up above the keyboard.
        el.focus({ preventScroll: true });
      }
    }
    wasDisabled.current = disabled;
  }, [disabled]);

  // Grow the textarea to fit its content instead of scrolling a one-line box.
  // Reset to 'auto' first so it can shrink back when text is deleted.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const full = el.scrollHeight;
    el.style.height = `${Math.min(full, MAX_INPUT_HEIGHT)}px`;
    el.style.overflowY = full > MAX_INPUT_HEIGHT ? 'auto' : 'hidden';
    setMultiline(full > 32);
  }, [value]);

  // Like WhatsApp: on a touch screen the Enter key starts a new line and the send
  // button sends; with a keyboard and mouse, Enter sends and Shift+Enter starts a line.
  const touch = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches);
  // Whether the box had the focus when the send button was pressed: it keeps it, so the keyboard stays open.
  const hadFocus = useRef(false);

  function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !touch && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSend();
    }
  }

  return (
    <div style={{ fontFamily: 'var(--font-sans)', ...style }}>
      <div
        style={{
          // Centre a single line; only sink to the baseline once the input has
          // grown, otherwise one line of text sits oddly at the bottom.
          display: 'flex', alignItems: multiline ? 'flex-end' : 'center', gap: 10,
          background: 'var(--surface)', border: '1px solid var(--line)',
          // Relax the pill into a rounded box once the input has grown, so
          // multi-line text doesn't collide with the curved edges.
          borderRadius: multiline ? 20 : 'var(--r-pill)',
          padding: compact ? '6px 8px 6px 18px' : '10px 14px 10px 20px',
        }}
      >
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKey}
          maxLength={maxLength}
          placeholder={placeholder}
          rows={1}
          enterKeyHint={touch ? 'enter' : 'send'}
          disabled={disabled}
          style={{
            flex: 1, background: 'transparent', color: 'var(--text)',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--chat-fs)', lineHeight: 'var(--chat-lh)',
            border: 'none', outline: 'none', resize: 'none', minHeight: 20, padding: 0,
          }}
        />
        <button
          type="button"
          onPointerDown={() => { hadFocus.current = document.activeElement === inputRef.current; }}
          onClick={() => {
            onSend();
            // Pressing a button can take the focus from the box, which closes the keyboard.
            if (hadFocus.current) inputRef.current?.focus({ preventScroll: true });
          }}
          // Clicking a button natively shifts DOM focus onto it — left
          // unchecked, that's what forced a tap back into the textarea
          // after every send. Suppressing mousedown's default action stops
          // the focus shift without affecting the click itself, so the
          // textarea (and on mobile, its on-screen keyboard) never loses
          // focus in the first place — no refocus-after-the-fact needed.
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled || !value.trim()}
          aria-label="Send"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          // A 44 x 44 pt tap area (Apple's minimum) around the round button, drawn smaller; the negative
          // margin keeps the box from growing.
          style={{
            flex: 'none', width: 44, height: 44, margin: compact ? -5 : -6, padding: 0,
            background: 'transparent', border: 'none',
            cursor: value.trim() && !disabled ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <span
            style={{
              width: compact ? 34 : 32, height: compact ? 34 : 32, borderRadius: '50%',
              // Quiet --line disc until there's something to send.
              background: value.trim() && !disabled ? 'var(--text)' : 'var(--line)',
              color: value.trim() && !disabled ? 'var(--bg)' : 'var(--text-dim)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .15s, color .15s, opacity .15s',
              opacity: hover && value.trim() && !disabled ? 0.8 : 1,
            }}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
              <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
            </svg>
          </span>
        </button>
      </div>
      {disclaimer ? (
        <div
          style={{
            marginTop: compact ? 6 : 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 5, color: 'var(--text-dim)', fontSize: 'var(--chat-fs-time)', opacity: 0.7,
            ...(compact ? { whiteSpace: 'nowrap', overflow: 'hidden' } : null),
          }}
        >
          <i className="ti ti-shield" style={{ fontSize: 'calc(11px * var(--text-scale))', flex: 'none' }} />
          <span style={compact ? { overflow: 'hidden', textOverflow: 'ellipsis' } : undefined}>{disclaimer}</span>
        </div>
      ) : null}
    </div>
  );
}

export default ChatComposer;
